import { faCalendar, faCircleInfo, faPlus, faXmark } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useQueryClient } from '@tanstack/react-query';
import { Fragment, useState } from 'react';
import { Controller, useFieldArray, useForm, useWatch } from 'react-hook-form';

import JurisdictionSelect from '@/components/select/JurisdictionSelect';
import { SessionChipOption } from '@/components/SessionAttendanceChips';
import { DAY_LABELS, DAY_OF_WEEK_OPTIONS } from '@/constants/dayOfWeek';
import { ELIGIBILITY_APPLICATION_KEY } from '@/constants/treatmentGroups';
import { US_TIMEZONE_OPTIONS } from '@/constants/usTimezones';
import FieldError from '@/forms/FieldError';
import MemberRow from '@/forms/TreatmentGroupForm/MemberRow';
import {
  Props,
  TreatmentGroupFormValues,
  TreatmentGroupSessionFormValues
} from '@/forms/TreatmentGroupForm/types';
import useJurisdictionEligibility from '@/forms/TreatmentGroupForm/useJurisdictionEligibility';
import { useGroupAppointmentTypeOptions } from '@/hooks/useAppointmentTypes/useAppointmentTypes';
import useLocalLogicQueryBuilder from '@/hooks/useLocalLogicQueryBuilder';
import { useLogicApplication } from '@/hooks/useLogicApplication';
import { useLogicGroups } from '@/hooks/useLogicGroups';
import useMemberEligibilityCheck from '@/hooks/useMemberEligibilityCheck';
import useStaff from '@/hooks/useStaff';
import { useCreateTreatmentGroup, useUpdateTreatmentGroup } from '@/hooks/useTreatmentGroups';
import { TreatmentGroupPayload } from '@/types/treatmentGroup';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Alert,
  Button,
  Input,
  InputGroup,
  Modal,
  MondayPicker,
  Pill,
  QueryBuilder,
  RadioGroup,
  Select,
  Tooltip
} from '@/ui';
import { OptionType } from '@/ui/components/Form/Select/types';
import Heading from '@/ui/components/Heading';
import { cn } from '@/ui/lib/utils';
import {
  daysUntil,
  formatOptionalCalendarDate,
  formatTimeOfDay,
  getNextMonday,
  getThisWeeksMonday,
  parseIsoDate,
  todayLocalDateString
} from '@/utils/dates';
import { toEligibilityPayload } from '@/utils/eligibilityPayload';
import {
  displayAsOf,
  groupHasStarted,
  lettersForSessionIds,
  sessionLetterMap
} from '@/utils/treatmentGroupSessions';

const ACCEPTING_NEW_PATIENTS_OPTIONS = [
  {
    label: (
      <span className="flex flex-col">
        <span>Yes</span>
        <span className="text-xs font-normal text-uniform-gray-500 dark:text-uniform-gray-400">
          Open for enrollment
        </span>
      </span>
    ),
    value: true
  },
  {
    label: (
      <span className="flex flex-col">
        <span>No</span>
        <span className="text-xs font-normal text-uniform-gray-500 dark:text-uniform-gray-400">
          Hidden from enrollment
        </span>
      </span>
    ),
    value: false
  }
];

const defaultValues = (treatmentGroup?: Props['treatmentGroup']): TreatmentGroupFormValues => {
  if (!treatmentGroup) {
    return {
      acceptingNewPatients: true,
      appointmentType: null,
      capacity: '',
      endDate: null,
      memberships: [],
      name: '',
      sessions: [{ dayOfWeek: null, staffId: null, startTime: '' }],
      startDate: null,
      timezone: null
    };
  }

  const timezoneOption =
    US_TIMEZONE_OPTIONS.find((option) => option.value === treatmentGroup.timezone) ??
    (treatmentGroup.timezone
      ? { label: treatmentGroup.timezone, value: treatmentGroup.timezone }
      : null);

  return {
    acceptingNewPatients: treatmentGroup.accepting_new_patients,
    appointmentType: {
      label: treatmentGroup.appointment_type_name,
      value: treatmentGroup.appointment_type_id
    },
    capacity: String(treatmentGroup.capacity),
    endDate: treatmentGroup.end_date,
    memberships: treatmentGroup.members.map((member) => ({
      endDate: member.end_on,
      membershipId: member.id,
      person: { avatar: null, label: member.person_name, value: member.person_id },
      startDate: member.start_on
    })),
    name: treatmentGroup.name,
    sessions:
      treatmentGroup.sessions.length > 0
        ? treatmentGroup.sessions.map((session) => ({
            dayOfWeek:
              DAY_OF_WEEK_OPTIONS.find((option) => option.value === String(session.day_of_week)) ??
              null,
            effectiveFrom: session.effective_from,
            effectiveUntil: session.effective_until,
            sessionId: session.id,
            staffId: { label: session.staff_name, value: session.staff_id },
            startTime: session.start_time
          }))
        : [{ dayOfWeek: null, staffId: null, startTime: '' }],
    startDate: treatmentGroup.start_date,
    timezone: timezoneOption
  };
};

// start_date and is_draft are set per submit action (draft vs. publish vs. save
// an already-published group), so they're layered on in `persist` rather than
// derived from the form -- the effective date is no longer a form field.
//
// `publishEffectiveDate` is only set when this save is the draft/new-group ->
// published transition (see `persist`) -- the backend treats every session as
// fully editable during exactly that save (GroupSession.sessions_are_editable
// mirrors the group's pre-save is_draft), so every session, new or already
// saved, gets the chosen effective date. Outside that transition, a new
// session on a still-draft group starts this week rather than next: nothing
// is live yet to disrupt, and dating it to the current week is what lets it
// show up as an in-effect (rather than upcoming) selection while the group
// is staged. A new session on an already-published group instead defaults
// to next Monday, and an existing session keeps whatever effective_from it
// already has.
const toPayload = (
  values: TreatmentGroupFormValues,
  publishEffectiveDate: string | null = null,
  isDraft = false,
  // Sorted (day_of_week, start_time) -> letter, matching how the backend
  // will label these same sessions once they're persisted -- lets a
  // member's selection be submitted as letters even for a brand-new
  // group's own not-yet-saved sessions.
  sessionLetters: Map<string, string> = new Map()
): Partial<TreatmentGroupPayload> => ({
  accepting_new_patients: values.acceptingNewPatients,
  appointment_type_id: values.appointmentType?.value,
  capacity: Number(values.capacity),
  end_date: values.endDate,
  members: values.memberships
    .filter((membership) => membership.person)
    .map((membership) => ({
      ...(membership.membershipId && { id: membership.membershipId }),
      end_on: membership.endDate ?? null,
      ...(membership.groupSessionIds !== undefined && {
        partial_session_letters: lettersForSessionIds(membership.groupSessionIds, sessionLetters)
      }),
      person_id: membership.person!.value,
      start_on: membership.startDate ?? (isDraft ? getThisWeeksMonday() : todayLocalDateString())
    })),
  name: values.name,
  sessions: values.sessions.map((session) => ({
    ...(session.sessionId && { id: session.sessionId }),
    day_of_week: Number(session.dayOfWeek?.value),
    effective_from:
      publishEffectiveDate ??
      (session.sessionId
        ? (session.effectiveFrom ?? '')
        : isDraft
          ? getThisWeeksMonday()
          : getNextMonday()),
    ...(session.effectiveUntil && { effective_until: session.effectiveUntil }),
    staff_id: session.staffId?.value ?? '',
    start_time: session.startTime
  })),
  timezone: values.timezone?.value ?? ''
});

const TreatmentGroupForm = ({ onCancel, onSuccess, treatmentGroup }: Props) => {
  const isEdit = !!treatmentGroup?.id;
  const queryClient = useQueryClient();
  const { data: appointmentTypes } = useGroupAppointmentTypeOptions();
  const { data: staffResponse } = useStaff({ withHealthieId: true });
  const createTreatmentGroup = useCreateTreatmentGroup();
  const updateTreatmentGroup = useUpdateTreatmentGroup();

  const { data: eligibilityApplication } = useLogicApplication(ELIGIBILITY_APPLICATION_KEY);
  const { data: existingEligibilityData, isFetching: isFetchingExistingEligibility } =
    useLogicGroups(eligibilityApplication?.id, treatmentGroup?.id);
  // A cached-but-stale query still returns its old value synchronously while it
  // refetches in the background -- treat that as "not ready yet" so the builder
  // below doesn't permanently seed from a stale (e.g. pre-save) empty tree.
  const existingEligibility =
    isEdit && !isFetchingExistingEligibility ? existingEligibilityData : undefined;
  const eligibilityBuilder = useLocalLogicQueryBuilder({
    applicationKey: ELIGIBILITY_APPLICATION_KEY,
    initialRootGroups: isEdit ? existingEligibility : []
  });
  const jurisdictionEligibility = useJurisdictionEligibility(eligibilityBuilder);

  const invalidateEligibility = (subjectId?: string) => {
    if (!eligibilityApplication?.id || !subjectId) return;
    queryClient.invalidateQueries({
      queryKey: ['getLogicGroups', eligibilityApplication.id, subjectId]
    });
  };

  const {
    control,
    formState: { errors },
    getValues,
    handleSubmit
  } = useForm({ defaultValues: defaultValues(treatmentGroup) });

  const { append, fields, remove, update } = useFieldArray({ control, name: 'sessions' });
  const [pendingRemoveIndex, setPendingRemoveIndex] = useState<number | null>(null);
  // Tracks the row just added via "Add session"/"Add member" so only that
  // row autofocuses -- every other row re-renders on each keystroke, and an
  // index comparison (rather than a one-shot ref) survives that.
  const [newSessionIndex, setNewSessionIndex] = useState<number | null>(null);
  const [newMemberIndex, setNewMemberIndex] = useState<number | null>(null);

  const memberFields = useFieldArray({ control, name: 'memberships' });
  const watchedMemberships = useWatch({ control, name: 'memberships' }) ?? [];
  const watchedSessions = useWatch({ control, name: 'sessions' }) ?? [];
  const watchedAppointmentType = useWatch({ control, name: 'appointmentType' });
  const memberPersonIds = watchedMemberships
    .map((membership) => membership.person?.value)
    .filter((value): value is string => !!value);
  const sessionStaffIds = watchedSessions
    .map((session) => session.staffId?.value)
    .filter((value): value is string => !!value);
  const { data: memberEligibilityStatuses } = useMemberEligibilityCheck({
    appointmentTypeId: watchedAppointmentType?.value ?? null,
    eligibility: toEligibilityPayload(eligibilityBuilder.rootGroups),
    personIds: memberPersonIds,
    staffIds: sessionStaffIds
  });

  // A brand-new group or an existing draft can be saved as a draft or published;
  // an already-published group just saves in place (its effective date is fixed).
  const isDraftGroup = treatmentGroup?.is_draft ?? false;
  const isEndedGroup =
    !!treatmentGroup?.end_date && daysUntil(`${treatmentGroup.end_date}T00:00:00`) <= 0;
  const isPublishedGroup = isEdit && !isDraftGroup && !isEndedGroup;
  const showDraftActions = !isEdit || isDraftGroup;
  const restrictedFieldsDisabled = isPublishedGroup || isEndedGroup;
  // A new session would start on/after the group's end date, which the backend rejects.
  const newSessionEffectiveFrom = isDraftGroup ? getThisWeeksMonday() : getNextMonday();
  const canAddSession =
    !treatmentGroup?.end_date || newSessionEffectiveFrom < treatmentGroup.end_date;
  // Holds the validated form values while the publish modal collects the
  // effective date; non-null also drives the modal open.
  const [publishValues, setPublishValues] = useState<TreatmentGroupFormValues | null>(null);
  const [publishDate, setPublishDate] = useState(getNextMonday());

  const appointmentTypeOptions = (appointmentTypes ?? []).map((appointmentType) => ({
    label: appointmentType.name,
    value: appointmentType.id
  }));

  const staffOptions = (staffResponse?.data ?? []).map((staff) => ({
    label: `${staff.first_name ?? ''} ${staff.last_name ?? ''}`.trim(),
    value: staff.id
  }));

  const isPending = createTreatmentGroup.isPending || updateTreatmentGroup.isPending;

  // A session with a future effectiveUntil is still meeting -- it's ending
  // soon, not ended -- so it's represented twice: once in the main list
  // (locked, since it's already scheduled to go) and once in the Pending
  // Changes section. Truly-past sessions get their own dimmed "Ended
  // sessions" section instead. `T00:00:00` pins the YYYY-MM-DD to local
  // midnight so daysUntil doesn't parse it as UTC and mis-bucket a session by
  // a day for viewers in negative-offset zones.
  const isEndedSession = (field: TreatmentGroupSessionFormValues) =>
    !!field.effectiveUntil && daysUntil(`${field.effectiveUntil}T00:00:00`) <= 0;
  const endedFields = fields
    .map((field, index) => ({ field, index }))
    .filter(({ field }) => isEndedSession(field));
  const isEndingSoonSession = (field: TreatmentGroupSessionFormValues) =>
    !!field.effectiveUntil && !isEndedSession(field);
  const pendingRemoveFields = fields
    .map((field, index) => ({ field, index }))
    .filter(({ field }) => isEndingSoonSession(field));
  // A draft's rows are all still editable regardless of their effective_from,
  // so this bucket only applies to a persisted session on a live group. Unlike
  // ending-soon sessions, a not-yet-started one has no place in the main list
  // -- it isn't part of the group's current schedule yet -- so it appears
  // only here. Also requires the group itself to have already started:
  // otherwise this is just part of the group's not-yet-live initial
  // schedule, not a change added mid-lifecycle, so it belongs in the main
  // list instead.
  const pendingAddFields = fields
    .map((field, index) => ({ field, index }))
    .filter(
      ({ field }) =>
        !isDraftGroup &&
        !!treatmentGroup &&
        groupHasStarted(treatmentGroup) &&
        !field.effectiveUntil &&
        !!field.sessionId &&
        daysUntil(`${field.effectiveFrom}T00:00:00`) > 0
    );
  const pendingAddIds = new Set(pendingAddFields.map(({ field }) => field.id));
  const visibleFields = fields.filter(
    (field) => !isEndedSession(field) && !pendingAddIds.has(field.id)
  );
  const hasPendingChanges = pendingAddFields.length > 0 || pendingRemoveFields.length > 0;
  const pendingChangesEffectiveDate = getNextMonday();

  // While creating a brand-new group every session is still unsaved (no
  // sessionId yet), but the picker still shows and lets a member's chips be
  // toggled against each row's local field.id -- sessionLetters (below)
  // translates that id into the letter the session will actually get once
  // persisted (sorted by day/time, same as the backend), so a selection
  // made here still resolves correctly after the group is created. Editing
  // an existing group is different: a newly-added session there sits
  // alongside already-saved ones a member CAN be assigned to, and a
  // touched selection including that session's fallback id would resolve
  // to no letter at all (it isn't part of this week's schedule yet) -- so
  // there, a still-unsaved session is excluded from the options entirely
  // rather than shown with a fallback id.
  // Otherwise scoped to visibleFields only (the group's current, in-effect
  // schedule) -- a pending-add session hasn't started yet, so a member
  // can't be enrolled in it until it actually takes effect. A session
  // that's ending soon is still selectable: members on it remain enrolled
  // up to its effective_until, so it hasn't stopped being "current" yet.
  // visibleFields' isEndedSession only catches a session whose
  // effective_until has already passed today -- it misses a row that was
  // superseded before the group ever went live (an edit made pre-launch end-
  // dates the old row to the group's own future start_date, so today's date
  // never falls past it). Re-checking effective_until against displayAsOf
  // here catches that case too, so a superseded row doesn't sit alongside
  // its replacement and hand out an extra letter the backend's own
  // display_as_of-scoped letter map doesn't recognize.
  const scheduledAsOf = treatmentGroup ? displayAsOf(treatmentGroup) : todayLocalDateString();
  const scheduledSessions = visibleFields
    .filter((field) => !isEdit || !!field.sessionId)
    .filter((field) => !field.effectiveUntil || field.effectiveUntil > scheduledAsOf)
    .map((field) => {
      // useFieldArray's `fields` only carries a row's value as of when it
      // was appended/updated -- day/time typed into a still-unsaved row
      // live in watchedSessions instead, keyed by the row's current index.
      const liveValues = watchedSessions[fields.findIndex((f) => f.id === field.id)];
      return {
        id: field.sessionId ?? field.id,
        dayOfWeek: liveValues?.dayOfWeek,
        startTime: liveValues?.startTime
      };
    })
    .filter(
      (session): session is { id: string; dayOfWeek: OptionType<string>; startTime: string } =>
        !!session.dayOfWeek && !!session.startTime
    );

  const sessionOptions: SessionChipOption[] = scheduledSessions.map((session) => ({
    id: session.id,
    label: `${DAY_LABELS[Number(session.dayOfWeek.value)]} · ${formatTimeOfDay(session.startTime)}`
  }));

  // Sorted (day_of_week, start_time) -> letter, matching how the backend
  // labels these same sessions -- passed to toPayload so a member's
  // chip-selected ids can be submitted as letters.
  const sessionLetters = sessionLetterMap(
    scheduledSessions.map((session) => ({
      id: session.id,
      dayOfWeek: Number(session.dayOfWeek.value),
      startTime: session.startTime
    }))
  );

  // Real, currently-persisted session ids per existing member -- used only
  // to seed a row's initial chip display (see MemberRow), never written
  // into the form itself. Keeping it out of the form means an untouched
  // member still omits partial_session_letters on submit (letting the
  // backend leave their real selection, including "every session" via a
  // null partial_sessions, untouched) instead of freezing it into an
  // explicit list that would silently exclude any session added later.
  const attendingSessionIdsByMembershipId = new Map(
    (treatmentGroup?.members ?? []).map((member) => [member.id, member.attending_group_session_ids])
  );

  const requestRemoveSession = (index: number) => {
    if (fields[index].sessionId && !isDraftGroup) {
      setPendingRemoveIndex(index);
    } else {
      remove(index);
    }
  };

  const confirmRemoveSession = () => {
    if (pendingRemoveIndex === null) return;
    update(pendingRemoveIndex, {
      ...getValues(`sessions.${pendingRemoveIndex}`),
      effectiveUntil: getNextMonday()
    });
    setPendingRemoveIndex(null);
  };

  const persist = (
    values: TreatmentGroupFormValues,
    { isDraft, startDate }: { isDraft: boolean; startDate: string | null }
  ) => {
    // In edit mode the builder starts empty and only seeds once the saved tree
    // has loaded. Until then, omit eligibility entirely (the backend leaves an
    // absent tree untouched) so a quick submit can't clear saved criteria with
    // a not-yet-loaded empty tree. On create there's nothing to wait for.
    const eligibilityReady = !isEdit || existingEligibility !== undefined;
    // `showDraftActions` (still true here) means this save is a new group or
    // an existing draft; publishing one of those (isDraft false) is the
    // draft/new -> published transition sessions need their effective date
    // reconciled for.
    const isPublishTransition = !isDraft && showDraftActions;
    const payload = {
      ...toPayload(values, isPublishTransition ? startDate : null, isDraft, sessionLetters),
      is_draft: isDraft,
      start_date: startDate,
      ...(eligibilityReady && {
        eligibility: toEligibilityPayload(eligibilityBuilder.rootGroups)
      })
    };

    if (isEdit) {
      updateTreatmentGroup.mutate(
        { id: treatmentGroup!.id, data: payload },
        {
          onSuccess: () => {
            invalidateEligibility(treatmentGroup!.id);
            onSuccess();
          }
        }
      );
    } else {
      createTreatmentGroup.mutate(payload, {
        onSuccess: (group) => {
          invalidateEligibility(group.id);
          onSuccess();
        }
      });
    }
  };

  // Save as draft: no effective date yet. Save an already-published group:
  // preserve its existing date (the form doesn't expose it). Publish: collect
  // the effective date via the modal first, then persist with is_draft=false.
  const saveDraft = handleSubmit((values) => persist(values, { isDraft: true, startDate: null }));
  const saveChanges = handleSubmit((values) =>
    persist(values, { isDraft: false, startDate: treatmentGroup?.start_date ?? null })
  );
  const openPublishModal = handleSubmit((values) => {
    setPublishDate(getNextMonday());
    setPublishValues(values);
  });
  const confirmPublish = () => {
    if (!publishValues) return;
    persist(publishValues, { isDraft: false, startDate: publishDate });
    setPublishValues(null);
  };

  return (
    <form
      onSubmit={showDraftActions ? (event) => event.preventDefault() : saveChanges}
      className="space-y-4">
      {isEdit &&
        treatmentGroup?.end_date &&
        (isEndedGroup ? (
          <Alert
            variant="error"
            message={`This group and all of its sessions ended on ${formatOptionalCalendarDate(treatmentGroup.end_date)}.`}
          />
        ) : (
          <Alert
            variant="info"
            message={`This group and all of its sessions will end on ${formatOptionalCalendarDate(treatmentGroup.end_date)}.`}
          />
        ))}

      <InputGroup>
        <Controller
          name="name"
          control={control}
          rules={{ required: 'Name is required' }}
          render={({ field }) => (
            <Input
              {...field}
              id="treatment-group-name"
              label="Name"
              placeholder="e.g. Ohio IOP"
              disabled={isEndedGroup}
            />
          )}
        />
        <FieldError message={errors.name?.message} />
      </InputGroup>

      <InputGroup>
        <JurisdictionSelect
          multiple
          selectedJurisdictionIds={jurisdictionEligibility.selectedJurisdictionIds}
          onChange={jurisdictionEligibility.onChangeSelectedJurisdictionIds}
          disabled={restrictedFieldsDisabled}
        />
      </InputGroup>

      <div className="grid grid-cols-2 gap-3">
        <InputGroup>
          <Controller
            name="appointmentType"
            control={control}
            rules={{ validate: (value) => !!value || 'Appointment type is required' }}
            render={({ field: { onChange, value } }) => (
              <Select
                label="Appointment type"
                options={appointmentTypeOptions}
                selectedOption={value}
                onChange={onChange}
                placeholder="Select..."
                disabled={restrictedFieldsDisabled}
              />
            )}
          />
          <FieldError message={errors.appointmentType?.message} />
        </InputGroup>

        <InputGroup>
          <Controller
            name="timezone"
            control={control}
            rules={{ validate: (value) => !!value || 'Timezone is required' }}
            render={({ field: { onChange, value } }) => (
              <Select
                ariaLabel="Timezone"
                label={
                  <span className="inline-flex items-center gap-1">
                    Timezone
                    <Tooltip content="Timezone should be set to the timezone of the jurisdiction">
                      <FontAwesomeIcon
                        icon={faCircleInfo}
                        className="text-uniform-gray-400"
                        aria-label="Timezone info"
                        role="img"
                      />
                    </Tooltip>
                  </span>
                }
                options={US_TIMEZONE_OPTIONS}
                selectedOption={value}
                onChange={onChange}
                placeholder="Select..."
                disabled={restrictedFieldsDisabled}
              />
            )}
          />
          <FieldError message={errors.timezone?.message} />
        </InputGroup>
      </div>

      <InputGroup>
        <Controller
          name="capacity"
          control={control}
          rules={{
            required: 'Max capacity is required',
            validate: (value) => Number(value) > 0 || 'Max capacity must be greater than 0'
          }}
          render={({ field }) => (
            <Input
              {...field}
              id="treatment-group-capacity"
              label="Capacity"
              placeholder="Max members"
              type="number"
              min={1}
              disabled={isEndedGroup}
            />
          )}
        />
        <FieldError message={errors.capacity?.message} />
      </InputGroup>

      <InputGroup className="mb-6 border-b border-uniform-gray-200 pb-6 dark:border-uniform-gray-700">
        <div className="mb-1 flex items-center justify-between">
          <Heading value={4} text="Group Sessions" />
          {hasPendingChanges && (
            <Pill color="warning" icon={faCalendar} size="sm" variant="subtle">
              Changes apply {formatOptionalCalendarDate(pendingChangesEffectiveDate)}
            </Pill>
          )}
        </div>
        <p className="mb-3 text-xs text-uniform-gray-500 dark:text-uniform-gray-400">
          Session changes take effect on the next Monday.
        </p>
        {/* A shared grid (rather than a flex row per session) keeps columns aligned across
            rows regardless of each row's own content -- independent flex rows can each
            distribute width differently and drift out of alignment. */}
        <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_auto] items-start gap-x-2 gap-y-3">
          <p className="text-sm font-medium text-uniform-gray-700 dark:text-uniform-gray-300">
            Day
          </p>
          <p className="text-sm font-medium text-uniform-gray-700 dark:text-uniform-gray-300">
            Start time
          </p>
          <p className="text-sm font-medium text-uniform-gray-700 dark:text-uniform-gray-300">
            Staff
          </p>
          <span aria-hidden />

          {fields.map((field, index) => {
            if (isEndedSession(field) || pendingAddIds.has(field.id)) return null;

            const isEndingSoon = isEndingSoonSession(field);
            const isLocked =
              isEndedGroup || isEndingSoon || (isPublishedGroup && !!field.sessionId);
            const lockedTooltip = "This can't be edited. Create a new session instead.";

            return (
              <Fragment key={field.id}>
                <div>
                  <Tooltip content={lockedTooltip} disabled={!isLocked}>
                    <div className="w-full">
                      <Controller
                        name={`sessions.${index}.dayOfWeek`}
                        control={control}
                        rules={{ validate: (value) => !!value || 'Day is required' }}
                        render={({ field: { onChange, value } }) => (
                          <Select
                            ariaLabel="Day"
                            autoFocus={index === newSessionIndex}
                            showLabel={false}
                            options={DAY_OF_WEEK_OPTIONS}
                            selectedOption={value}
                            onChange={onChange}
                            placeholder="Day..."
                            disabled={isLocked}
                          />
                        )}
                      />
                    </div>
                  </Tooltip>
                  <FieldError message={errors.sessions?.[index]?.dayOfWeek?.message} />
                </div>

                <div>
                  <Tooltip content={lockedTooltip} disabled={!isLocked}>
                    <div className="w-full">
                      <Controller
                        name={`sessions.${index}.startTime`}
                        control={control}
                        rules={{ required: 'Start time is required' }}
                        render={({ field }) => (
                          <Input
                            {...field}
                            id={`treatment-group-session-${index}-start-time`}
                            aria-label="Start time"
                            type="time"
                            disabled={isLocked}
                          />
                        )}
                      />
                    </div>
                  </Tooltip>
                  <FieldError message={errors.sessions?.[index]?.startTime?.message} />
                </div>

                <div>
                  <Tooltip content={lockedTooltip} disabled={!isLocked}>
                    <div className="w-full">
                      <Controller
                        name={`sessions.${index}.staffId`}
                        control={control}
                        rules={{ validate: (value) => !!value || 'Staff is required' }}
                        render={({ field: { onChange, value } }) => (
                          <Select
                            ariaLabel="Staff"
                            showLabel={false}
                            options={staffOptions}
                            selectedOption={value}
                            onChange={onChange}
                            placeholder="Staff..."
                            disabled={isLocked}
                          />
                        )}
                      />
                    </div>
                  </Tooltip>
                  <FieldError message={errors.sessions?.[index]?.staffId?.message} />
                </div>

                {isEndingSoon ? (
                  <Pill size="sm" variant="subtle" color="warning" className="mt-2 inline w-fit">
                    Ends {formatOptionalCalendarDate(field.effectiveUntil)}
                  </Pill>
                ) : !isEndedGroup && visibleFields.length > 1 ? (
                  <button
                    type="button"
                    aria-label="Remove session"
                    onClick={() => requestRemoveSession(index)}
                    className="mt-2 shrink-0 text-uniform-gray-400 hover:text-uniform-red-600">
                    <FontAwesomeIcon icon={faXmark} />
                  </button>
                ) : (
                  <span aria-hidden />
                )}
              </Fragment>
            );
          })}
        </div>
        {canAddSession && (
          <Button
            type="button"
            variant="outline"
            color="neutral"
            size="sm"
            leftIcon={faPlus}
            className="mt-3"
            onClick={() => {
              setNewSessionIndex(fields.length);
              append({ dayOfWeek: null, staffId: null, startTime: '' });
            }}>
            Add session
          </Button>
        )}

        {hasPendingChanges && (
          <div className="mt-4 rounded-lg border border-dashed border-uniform-gray-300 p-3 dark:border-uniform-gray-600">
            <div className="mb-3 flex items-center justify-between">
              <p className="text-xs font-medium uppercase tracking-wide text-uniform-gray-500 dark:text-uniform-gray-400">
                Upcoming changes
              </p>
              <Pill size="sm" variant="subtle" color="secondary">
                Effective {formatOptionalCalendarDate(pendingChangesEffectiveDate)}
              </Pill>
            </div>
            <div className="grid grid-cols-[auto_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)] items-start gap-x-2 gap-y-3">
              <span aria-hidden />
              <p className="text-sm font-medium text-uniform-gray-700 dark:text-uniform-gray-300">
                Day
              </p>
              <p className="text-sm font-medium text-uniform-gray-700 dark:text-uniform-gray-300">
                Start time
              </p>
              <p className="text-sm font-medium text-uniform-gray-700 dark:text-uniform-gray-300">
                Staff
              </p>

              {pendingAddFields.map(({ field, index }) => (
                <Fragment key={field.id}>
                  <Pill size="sm" variant="subtle" color="primary" className="mt-2 inline w-fit">
                    + Adding
                  </Pill>

                  <Controller
                    name={`sessions.${index}.dayOfWeek`}
                    control={control}
                    render={({ field: { value } }) => (
                      <Select
                        ariaLabel="Day"
                        showLabel={false}
                        options={DAY_OF_WEEK_OPTIONS}
                        selectedOption={value}
                        onChange={() => {}}
                        disabled
                      />
                    )}
                  />

                  <Controller
                    name={`sessions.${index}.startTime`}
                    control={control}
                    render={({ field: { value } }) => (
                      <Input value={value} readOnly aria-label="Start time" type="time" disabled />
                    )}
                  />

                  <Controller
                    name={`sessions.${index}.staffId`}
                    control={control}
                    render={({ field: { value } }) => (
                      <Select
                        ariaLabel="Staff"
                        showLabel={false}
                        options={staffOptions}
                        selectedOption={value}
                        onChange={() => {}}
                        disabled
                      />
                    )}
                  />
                </Fragment>
              ))}

              {pendingRemoveFields.map(({ field, index }) => (
                <Fragment key={field.id}>
                  <Pill
                    size="sm"
                    variant="subtle"
                    color="destructive"
                    className="mt-2 inline w-fit">
                    − Removing
                  </Pill>

                  <Controller
                    name={`sessions.${index}.dayOfWeek`}
                    control={control}
                    render={({ field: { value } }) => (
                      <Select
                        ariaLabel="Day"
                        showLabel={false}
                        options={DAY_OF_WEEK_OPTIONS}
                        selectedOption={value}
                        onChange={() => {}}
                        disabled
                      />
                    )}
                  />

                  <Controller
                    name={`sessions.${index}.startTime`}
                    control={control}
                    render={({ field: { value } }) => (
                      <Input value={value} readOnly aria-label="Start time" type="time" disabled />
                    )}
                  />

                  <Controller
                    name={`sessions.${index}.staffId`}
                    control={control}
                    render={({ field: { value } }) => (
                      <Select
                        ariaLabel="Staff"
                        showLabel={false}
                        options={staffOptions}
                        selectedOption={value}
                        onChange={() => {}}
                        disabled
                      />
                    )}
                  />
                </Fragment>
              ))}
            </div>
          </div>
        )}

        {endedFields.length > 0 && (
          <div className="mt-4 border-t border-uniform-gray-200 pt-3 dark:border-uniform-gray-700">
            <p className="mb-3 text-sm font-medium text-uniform-gray-700 dark:text-uniform-gray-300">
              Ended sessions
            </p>
            <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_auto] items-start gap-x-2 gap-y-3 opacity-60">
              <p className="text-sm font-medium text-uniform-gray-700 dark:text-uniform-gray-300">
                Day
              </p>
              <p className="text-sm font-medium text-uniform-gray-700 dark:text-uniform-gray-300">
                Start time
              </p>
              <p className="text-sm font-medium text-uniform-gray-700 dark:text-uniform-gray-300">
                Staff
              </p>
              <p className="text-sm font-medium text-uniform-gray-700 dark:text-uniform-gray-300">
                Ended
              </p>

              {endedFields.map(({ field, index }) => (
                <Fragment key={field.id}>
                  <Controller
                    name={`sessions.${index}.dayOfWeek`}
                    control={control}
                    render={({ field: { value } }) => (
                      <Select
                        ariaLabel="Day"
                        showLabel={false}
                        options={DAY_OF_WEEK_OPTIONS}
                        selectedOption={value}
                        onChange={() => {}}
                        disabled
                      />
                    )}
                  />

                  <Controller
                    name={`sessions.${index}.startTime`}
                    control={control}
                    render={({ field: { value } }) => (
                      <Input value={value} readOnly aria-label="Start time" type="time" disabled />
                    )}
                  />

                  <Controller
                    name={`sessions.${index}.staffId`}
                    control={control}
                    render={({ field: { value } }) => (
                      <Select
                        ariaLabel="Staff"
                        showLabel={false}
                        options={staffOptions}
                        selectedOption={value}
                        onChange={() => {}}
                        disabled
                      />
                    )}
                  />

                  <p className="mt-2 text-sm text-uniform-gray-500 dark:text-uniform-gray-400">
                    {formatOptionalCalendarDate(field.effectiveUntil)}
                  </p>
                </Fragment>
              ))}
            </div>
          </div>
        )}
      </InputGroup>

      <InputGroup className="mb-6 border-b border-uniform-gray-200 pb-6 dark:border-uniform-gray-700">
        <div className="mb-3 flex items-center justify-between">
          <Heading value={4} text="Members" className="mb-0" />
          <p className="text-xs text-uniform-gray-500 dark:text-uniform-gray-400">
            Enrollment starts today
          </p>
        </div>
        <div className="space-y-4">
          {memberFields.fields.map((field, index) => {
            const person = watchedMemberships[index]?.person ?? null;
            const status = person ? memberEligibilityStatuses?.[person.value] : undefined;
            const membershipId = watchedMemberships[index]?.membershipId;

            return (
              <MemberRow
                key={field.id}
                autoFocus={index === newMemberIndex}
                control={control}
                currentGroupId={treatmentGroup?.id}
                errors={errors}
                index={index}
                initialAttendingSessionIds={
                  membershipId ? attendingSessionIdsByMembershipId.get(membershipId) : undefined
                }
                isEndedGroup={isEndedGroup}
                membershipId={membershipId}
                onRemove={() => memberFields.remove(index)}
                personId={person?.value ?? null}
                sessionOptions={sessionOptions}
                startDate={watchedMemberships[index]?.startDate ?? null}
                status={status}
              />
            );
          })}
        </div>
        {!isEndedGroup && (
          <Button
            type="button"
            variant="outline"
            color="neutral"
            size="sm"
            leftIcon={faPlus}
            className="mt-3"
            onClick={() => {
              setNewMemberIndex(memberFields.fields.length);
              memberFields.append({
                endDate: null,
                person: null,
                // Matches the draft's own session start (see toPayload) so the new
                // member's membership row lines up with display_as_of.
                startDate: showDraftActions ? getThisWeeksMonday() : todayLocalDateString()
              });
            }}>
            Add member
          </Button>
        )}
      </InputGroup>

      <InputGroup>
        <Controller
          name="acceptingNewPatients"
          control={control}
          render={({ field: { onChange, value } }) => (
            <RadioGroup
              id="treatment-group-accepting-new-patients"
              label="Accepting new patients"
              options={ACCEPTING_NEW_PATIENTS_OPTIONS}
              selectedValue={value}
              onChange={onChange}
              disabled={isEndedGroup}
            />
          )}
        />
      </InputGroup>

      <Accordion variant="default">
        <AccordionItem>
          <AccordionTrigger>Advanced</AccordionTrigger>
          <AccordionContent>
            <fieldset disabled={restrictedFieldsDisabled} className="contents">
              <div className={cn(restrictedFieldsDisabled && 'pointer-events-none opacity-50')}>
                <p className="mb-1 text-sm font-medium text-uniform-gray-700 dark:text-uniform-gray-300">
                  Eligibility criteria
                </p>
                <p className="mb-3 text-xs text-uniform-gray-500 dark:text-uniform-gray-400">
                  Members must match these criteria to be eligible for this group. Groups are
                  combined with AND; conditions within a group follow its operator.
                </p>
                <QueryBuilder
                  {...eligibilityBuilder}
                  hideFieldSelect
                  hideOperatorSelect
                  hideNegate
                  allowMultipleValues={false}
                  labels={{
                    valuePlaceholder: 'Select classification',
                    valueWithCustomPlaceholder: 'Select classification'
                  }}
                />
              </div>
            </fieldset>
          </AccordionContent>
        </AccordionItem>
      </Accordion>

      <div className="flex items-center gap-2">
        {showDraftActions ? (
          <>
            <Button
              type="button"
              onClick={openPublishModal}
              isLoading={isPending}
              disabled={isPending}>
              Publish
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={saveDraft}
              isLoading={isPending}
              disabled={isPending}>
              Save as draft
            </Button>
          </>
        ) : (
          <Button type="submit" isLoading={isPending} disabled={isPending}>
            Save changes
          </Button>
        )}
        <Button type="button" variant="outline" color="neutral" onClick={onCancel}>
          Cancel
        </Button>
      </div>

      <Modal
        open={pendingRemoveIndex !== null}
        onClose={() => setPendingRemoveIndex(null)}
        title="Remove group session?"
        description="You are removing a group session. Any members currently scheduled for this session will be affected. Are you sure you want to continue?"
        secondaryAction={{ label: 'Cancel', onClick: () => setPendingRemoveIndex(null) }}
        primaryAction={{
          label: 'Remove session',
          color: 'destructive',
          onClick: confirmRemoveSession
        }}
      />

      <Modal
        open={publishValues !== null}
        onClose={() => setPublishValues(null)}
        title="Set the effective date"
        description="Choose the Monday this group becomes effective."
        overflowVisible
        secondaryAction={{ label: 'Cancel', onClick: () => setPublishValues(null) }}
        primaryAction={{
          label: 'Publish',
          onClick: confirmPublish,
          isLoading: isPending,
          disabled: isPending
        }}>
        <InputGroup>
          <MondayPicker
            label="Effective date"
            name="publishDate"
            value={publishDate}
            onChange={(value) => setPublishDate(value ?? getNextMonday())}
            minDate={parseIsoDate(getThisWeeksMonday())}
          />
        </InputGroup>
      </Modal>
    </form>
  );
};

export default TreatmentGroupForm;
